import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, Clock, CheckSquare, Square, Loader2,
  PhoneCall, MessageSquare, AlertTriangle, User, Bot, Filter, Plus, X,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatError, leadApi } from "@/services/api";

const TYPE_FILTERS = [
  { code: "",         label: "All" },
  { code: "TASK",     label: "Tasks" },
  { code: "CALL_LOG", label: "Calls" },
  { code: "FOLLOW_UP", label: "Follow-ups" },
];

const ACTIVITY_TYPES = [
  { code: "TASK",     label: "Task" },
  { code: "CALL_LOG", label: "Call" },
  { code: "MEETING",  label: "Meeting" },
  { code: "NOTE",     label: "Note" },
];

export default function CRMActivities() {
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/crm/activities", {
        params: { ...(typeFilter && { activity_type: typeFilter }) },
      });
      setData(res.data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto space-y-5">
      {showCreate && (
        <LogActivityModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); setRefreshKey(k => k + 1); }}
        />
      )}

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-[#0176D3]" />
          <h1 className="text-xl font-bold text-[#181818] font-sans">Activities</h1>
          {!loading && summary.overdue_count > 0 && (
            <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded border bg-rose-500/10 border-rose-500/30 text-rose-400">
              <AlertTriangle className="w-3 h-3" /> {summary.overdue_count} overdue
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="log-activity-btn"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EEF4FF] border border-[#0176D3]/30 rounded-lg text-xs text-[#0176D3] hover:bg-[#EEF4FF] transition-colors font-medium"
          >
            <Plus className="w-3.5 h-3.5" /> Log Activity
          </button>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="p-2 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3] hover:border-[#0176D3]/30 transition-colors"
          >
            <Activity className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Type Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-[#706E6B]" />
        {TYPE_FILTERS.map(f => (
          <button
            key={f.code}
            onClick={() => setTypeFilter(f.code)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
              typeFilter === f.code
                ? "bg-[#EEF4FF] border-[#0176D3]/30 text-[#0176D3]"
                : "border-[#E5E5E5] text-[#706E6B] hover:text-[#444444] hover:border-[#C9C9C9]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4].map(i => <div key={i} className="h-14 bg-[#F3F3F3] rounded-lg animate-pulse" />)}
        </div>
      ) : !data ? null : (
        <div className="space-y-6">
          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Upcoming",   value: summary.upcoming_count   ?? 0, color: "text-[#0176D3]" },
              { label: "Overdue",    value: summary.overdue_count    ?? 0, color: "text-rose-400" },
              { label: "Tasks",      value: summary.total_tasks      ?? 0, color: "text-[#444444]" },
              { label: "Follow-ups", value: summary.total_follow_ups ?? 0, color: "text-[#444444]" },
            ].map(s => (
              <div key={s.label} className="bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-3 text-center">
                <p className={`text-xl font-bold font-sans ${s.color}`}>{s.value}</p>
                <p className="text-[10px] text-[#706E6B] uppercase tracking-wider mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          <ActivitySection title="Overdue"              items={data.overdue  || []} variant="overdue" onRefresh={() => setRefreshKey(k => k + 1)} />
          <ActivitySection title="Upcoming"             items={data.upcoming || []} variant="normal"  onRefresh={() => setRefreshKey(k => k + 1)} />
          <ActivitySection title="Recent / Completed"   items={data.recent   || []} variant="recent"  onRefresh={() => setRefreshKey(k => k + 1)} />
        </div>
      )}
    </div>
  );
}

// ── Activity Section ──────────────────────────────────────────────────────────

function ActivitySection({ title, items, variant, onRefresh }) {
  const titleColor = variant === "overdue" ? "text-rose-400" : "text-[#444444]";
  if (items.length === 0) {
    return (
      <div>
        <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${titleColor}`}>{title}</h4>
        <p className="text-xs text-[#706E6B] pl-1">No activities</p>
      </div>
    );
  }
  return (
    <div data-testid={`activities-${variant}`}>
      <h4 className={`text-xs font-semibold uppercase tracking-wider mb-2 ${titleColor}`}>
        {title} <span className="text-[#706E6B] font-normal normal-case">({items.length})</span>
      </h4>
      <div className="space-y-1.5">
        {items.map((item, i) => (
          <ActivityItem key={item.id || i} item={item} variant={variant} onRefresh={onRefresh} />
        ))}
      </div>
    </div>
  );
}

function ActivityItem({ item, variant, onRefresh }) {
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();

  const handleComplete = async () => {
    if (item._type !== "TASK") return;
    setCompleting(true);
    try {
      await api.patch(`/tasks/${item.id}/complete`, { notes: "" });
      toast.success("Task completed");
      onRefresh();
    } catch (e) { toast.error(formatError(e)); }
    finally { setCompleting(false); }
  };

  const isOverdue = variant === "overdue";

  if (item._type === "FOLLOW_UP") {
    return (
      <div className={`flex items-start gap-2.5 py-2.5 px-3.5 rounded-lg bg-white border ${isOverdue ? "border-rose-500/20" : "border-[#E5E5E5]"}`}>
        <Clock className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isOverdue ? "text-rose-400" : "text-[#8C4B00]"}`} />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-[#444444]">{item.reason || item.follow_up_type}</p>
          <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[#706E6B]">
            <span>{item.status === "COMPLETED" ? `Completed ${fmtRelative(item.completed_at)}` : `Scheduled ${fmtDate(item.scheduled_at)}`}</span>
            {item.entity_id && (
              <button
                onClick={() => navigate(`/engage/leads/${item.entity_id}`)}
                className="text-[#0176D3] hover:underline"
              >
                View Lead
              </button>
            )}
          </div>
        </div>
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${
          item.status === "COMPLETED" ? "bg-emerald-500/10 text-[#2E844A]" :
          isOverdue ? "bg-rose-500/10 text-rose-400" : "bg-amber-500/10 text-[#8C4B00]"
        }`}>{item.status}</span>
      </div>
    );
  }

  // TASK / CALL_LOG / MEETING / NOTE
  const isComplete = item.status === "COMPLETE";
  const isCall     = item.task_type === "CALL_LOG";
  const isMeeting  = item.task_type === "MEETING";
  const isNote     = item.task_type === "NOTE";

  return (
    <div className={`flex items-start gap-2.5 py-2.5 px-3.5 rounded-lg bg-white border ${isOverdue ? "border-rose-500/20" : "border-[#E5E5E5]"}`}>
      {isCall ? (
        <PhoneCall className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isComplete ? "text-[#2E844A]" : "text-[#706E6B]"}`} />
      ) : isMeeting ? (
        <Clock className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isComplete ? "text-[#2E844A]" : "text-[#0176D3]"}`} />
      ) : isNote ? (
        <MessageSquare className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[#706E6B]`} />
      ) : (
        <button onClick={handleComplete} disabled={isComplete || completing} className={`mt-0.5 flex-shrink-0 ${isComplete ? "text-[#2E844A] cursor-default" : "text-[#706E6B] hover:text-[#2E844A]"}`}>
          {completing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
           isComplete  ? <CheckSquare className="w-3.5 h-3.5" /> :
                        <Square className="w-3.5 h-3.5" />}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-xs ${isComplete ? "line-through text-[#706E6B]" : "text-[#444444]"}`}>
          {item.title}
          {isCall && item.call_outcome && (
            <span className="ml-2 text-[10px] font-mono text-[#706E6B]">{item.call_outcome}</span>
          )}
        </p>
        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-[#706E6B]">
          {item.due_date && <span>{fmtDate(item.due_date)}</span>}
          {isComplete && <span className="text-[#2E844A]">Completed {fmtRelative(item.completed_at)}</span>}
          {item.entity_id && (
            <button
              onClick={() => navigate(`/engage/leads/${item.entity_id}`)}
              className="text-[#0176D3] hover:underline"
            >
              View Lead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Log Activity Modal ─────────────────────────────────────────────────────────

const CALL_OUTCOMES = [
  { value: "REACHED",            label: "Reached" },
  { value: "NO_ANSWER",          label: "No Answer" },
  { value: "LEFT_VOICEMAIL",     label: "Left Voicemail" },
  { value: "CALLBACK_REQUESTED", label: "Callback Requested" },
  { value: "WRONG_NUMBER",       label: "Wrong Number" },
];

const mInp = "w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded-lg text-sm text-[#181818] focus:outline-none focus:border-[#0176D3]/50 focus:ring-1 focus:ring-[#0176D3]/20";

function LogActivityModal({ onClose, onCreated }) {
  const [leads,  setLeads]  = useState([]);
  const [form,   setForm]   = useState({ lead_id: "", task_type: "TASK", title: "", notes: "", due_date: "", call_outcome: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    leadApi.list({ limit: 50 }).then(r => setLeads(r.data.results || [])).catch(() => {});
  }, []);

  const set = (k, v) => { setForm(p => ({...p, [k]: v})); setErrors(p => ({...p, [k]: ""})); };

  const validate = () => {
    const e = {};
    if (!form.lead_id)                                                          e.lead_id   = "Related lead is required";
    if (!form.title.trim() && form.task_type !== "NOTE")                        e.title     = "Subject is required";
    if (form.task_type === "TASK" && !form.due_date)                            e.due_date  = "Due date is required for tasks";
    if (form.task_type === "MEETING" && !form.due_date)                         e.due_date  = "Meeting date / time is required";
    if (form.task_type === "NOTE" && !form.notes.trim())                        e.notes     = "Note content is required";
    if (form.task_type === "CALL_LOG" && !form.title.trim() && !form.notes.trim()) e.title = "Subject or notes required for call logs";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        task_type:    form.task_type,
        title:        form.title.trim() || (form.task_type === "NOTE" ? "Note" : ""),
        notes:        form.notes,
        ...(form.due_date     && { due_date:     new Date(form.due_date).toISOString() }),
        ...(form.call_outcome && { call_outcome: form.call_outcome }),
      };
      await api.post(`/leads/${form.lead_id}/tasks`, payload);
      toast.success("Activity logged");
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errs = {};
        detail.forEach(d => { errs[d.field] = d.message; });
        setErrors(errs);
      } else {
        toast.error(typeof detail === "string" ? detail : formatError(e));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="bg-white rounded-xl border border-[#E5E5E5] w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-[#E5E5E5]">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#0176D3]" />
            <h2 className="text-base font-semibold text-[#181818]">Log Activity</h2>
          </div>
          <button onClick={onClose}><X className="w-4 h-4 text-[#706E6B]" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Activity Type */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-2">Activity Type</label>
            <div className="grid grid-cols-4 gap-1 p-1 bg-[#F3F3F3] border border-[#E5E5E5] rounded-lg">
              {ACTIVITY_TYPES.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  data-testid={`activity-type-${code.toLowerCase()}`}
                  onClick={() => { set("task_type", code); setErrors({}); }}
                  className={`px-2 py-1.5 text-xs rounded transition-colors ${
                    form.task_type === code
                      ? "bg-[#0176D3] text-white font-medium"
                      : "text-[#706E6B] hover:text-[#444]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Related Lead */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">Related Lead <span className="text-rose-500">*</span></label>
            <select
              value={form.lead_id}
              onChange={e => set("lead_id", e.target.value)}
              data-testid="activity-lead-select"
              className={`${mInp}${errors.lead_id ? " border-rose-400" : ""}`}
            >
              <option value="">— Select Lead —</option>
              {leads.map(l => (
                <option key={l.id} value={l.id}>{l.lead_number} · {l.customer_name}</option>
              ))}
            </select>
            {errors.lead_id && <p data-testid="err-activity-lead" className="text-xs text-rose-500 mt-1">{errors.lead_id}</p>}
          </div>

          {/* Subject / Title */}
          {form.task_type !== "NOTE" && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">
                Subject {form.task_type !== "CALL_LOG" && <span className="text-rose-500">*</span>}
              </label>
              <input
                value={form.title}
                onChange={e => set("title", e.target.value)}
                placeholder={
                  form.task_type === "CALL_LOG" ? "Who was called / what was discussed" :
                  form.task_type === "MEETING"  ? "Meeting agenda or subject" :
                  "Task description"
                }
                data-testid="activity-title"
                className={`${mInp}${errors.title ? " border-rose-400" : ""}`}
              />
              {errors.title && <p data-testid="err-activity-title" className="text-xs text-rose-500 mt-1">{errors.title}</p>}
            </div>
          )}

          {/* Due Date — TASK / MEETING */}
          {(form.task_type === "TASK" || form.task_type === "MEETING") && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">
                {form.task_type === "MEETING" ? "Date & Time" : "Due Date"}
                <span className="text-rose-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={form.due_date}
                onChange={e => set("due_date", e.target.value)}
                data-testid="activity-due-date"
                className={`${mInp}${errors.due_date ? " border-rose-400" : ""}`}
              />
              {errors.due_date && <p data-testid="err-activity-due-date" className="text-xs text-rose-500 mt-1">{errors.due_date}</p>}
            </div>
          )}

          {/* Call Outcome — CALL_LOG only */}
          {form.task_type === "CALL_LOG" && (
            <div>
              <label className="block text-xs font-medium text-[#444] mb-1">Call Outcome</label>
              <select
                value={form.call_outcome}
                onChange={e => set("call_outcome", e.target.value)}
                data-testid="activity-call-outcome"
                className={mInp}
              >
                <option value="">— Select outcome —</option>
                {CALL_OUTCOMES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-[#444] mb-1">
              {form.task_type === "NOTE"
                ? <><span>Note Content</span> <span className="text-rose-500">*</span></>
                : "Notes"}
            </label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
              placeholder={form.task_type === "NOTE" ? "Enter note content…" : "Additional context…"}
              data-testid="activity-notes"
              className={`${mInp} resize-none${errors.notes ? " border-rose-400" : ""}`}
            />
            {errors.notes && <p data-testid="err-activity-notes" className="text-xs text-rose-500 mt-1">{errors.notes}</p>}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#706E6B] hover:text-[#181818]">Cancel</button>
          <button
            data-testid="activity-submit-btn"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 text-sm bg-[#0176D3] text-white rounded-lg hover:bg-[#0060B0] disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : "Log Activity"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Utilities ─────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
  catch { return ""; }
}

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch { return ""; }
}
